import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';

import {
  CURSOR_QUERY_PARAM,
  DataTable,
  LIMIT_QUERY_PARAM,
} from '@/components/ui/data-table';
import { TableTitle } from '@/components/ui/table-title';
import { AdminUser, SeekPage } from '@activepieces/shared';
import { adminApi } from '@/features/internal-admin/lib/admin-api';
import { useAdminUsersTable } from '@/features/internal-admin/lib/admin-users-table';
import { ClientDetailContext } from '../client-detail';

const ClientUsersTab = () => {
  const { columns, filters } = useAdminUsersTable();
  const filteredFilters = filters.filter(
    (f) => f.accessorKey !== 'externalCustomerId',
  );
  const { tenantId } = useOutletContext<ClientDetailContext>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery<SeekPage<AdminUser>, Error>({
    queryKey: ['admin-client-users', tenantId, searchParams.toString()],
    queryFn: () =>
      adminApi.listUsers({
        cursor: searchParams.get(CURSOR_QUERY_PARAM) ?? undefined,
        limit: Number(searchParams.get(LIMIT_QUERY_PARAM)) || 20,
        search: searchParams.get('email') ?? undefined,
        externalId: searchParams.get('externalId') ?? undefined,
        tenantId,
        status:
          searchParams.getAll('status').length > 0
            ? searchParams.getAll('status')
            : undefined,
      }),
    enabled: !!tenantId,
  });

  if (isError) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('Failed to load users')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full">
      <TableTitle>{t('Users')}</TableTitle>
      <DataTable
        columns={columns}
        page={data}
        isLoading={isLoading}
        filters={filteredFilters}
        hiddenColumns={['externalCustomerId']}
        onRowClick={(row, newWindow) => {
          const url = `/internal-admin/users/${row.id}`;
          if (newWindow) {
            window.open(url, '_blank');
          } else {
            navigate(url);
          }
        }}
      />
    </div>
  );
};

export default ClientUsersTab;
